
// import React from 'react';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Badge } from '@/components/ui/badge';
// import { useQuery } from '@tanstack/react-query';
// import { supabase } from '@/integrations/supabase/client';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
// import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Users, Globe } from 'lucide-react';
// import { indexedDBService } from '@/services/indexedDBService';

// const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// export const SupplyChainAnalytics = () => {
//   const { user } = useAuth();

//   const { data: suppliers, isLoading: suppliersLoading } = useQuery({
//     queryKey: ['suppliers-analytics', user?.id],
//     queryFn: async () => {
//       await indexedDBService.initialize();
//       const { data } = await indexedDBService.getAll<any>('suppliers');
//       return (data || []).filter((s) => s.user_id === user?.id);
//     },
//     enabled: !!user?.id,
//   });

//   const { data: assessments, isLoading: assessmentsLoading } = useQuery({
//     queryKey: ['assessments-analytics', user?.id],
//     queryFn: async () => {
//       await indexedDBService.initialize();
//       const { data } = await indexedDBService.getAll<any>('assessments');
//       return (data || []).filter((a) => a.user_id === user?.id);
//     },
//     enabled: !!user?.id,
//   });

//   // Derived metrics from assessments for enhanced analytics
//   const assessmentsCount = assessments?.length || 0;
//   const ghgDisclosureRate = assessmentsCount ? Math.round((assessments.filter((a: any) => a.ghg_disclosed).length / assessmentsCount) * 100) : 0;
//   const emsAdoptionRate = assessmentsCount ? Math.round((assessments.filter((a: any) => a.has_ems).length / assessmentsCount) * 100) : 0;
//   const sbtiCommitRate = assessmentsCount ? Math.round((assessments.filter((a: any) => a.sbti_committed).length / assessmentsCount) * 100) : 0;
//   const avgEcoVadis = assessmentsCount ? (assessments.reduce((sum: number, a: any) => sum + (Number(a.ecovadis_score) || 0), 0) / assessmentsCount) : 0;
//   const totalScope1 = assessments?.reduce((sum: number, a: any) => sum + (Number(a.scope1_emissions) || 0), 0) || 0;
//   const totalScope2 = assessments?.reduce((sum: number, a: any) => sum + (Number(a.scope2_emissions) || 0), 0) || 0;
//   const totalScope3 = assessments?.reduce((sum: number, a: any) => sum + (Number(a.scope3_emissions) || 0), 0) || 0;

//   if (suppliersLoading || assessmentsLoading) {
//     return <div className="flex justify-center p-8">Loading analytics...</div>;
//   }

//   // Calculate key metrics
//   const totalSuppliers = suppliers?.length || 0;
//   const activeSuppliers = suppliers?.filter(s => s.status === 'active').length || 0;
//   const avgESGScore = suppliers?.length 
//     ? suppliers.reduce((sum: number, s: any) => sum + (s.esg_assessment_score || 0), 0) / suppliers.length
//     : 0;
  
//   const highRiskSuppliers = suppliers?.filter((s: any) => s.risk_level === 'high').length || 0;
//   const compliantSuppliers = suppliers?.filter((s: any) => 
//     s.environmental_compliance && s.social_compliance && s.governance_compliance
//   ).length || 0;

//   // Prepare chart data
//   const riskDistribution = [
//     { name: 'Low Risk', value: suppliers?.filter((s: any) => s.risk_level === 'low').length || 0, color: '#00C49F' },
//     { name: 'Medium Risk', value: suppliers?.filter((s: any) => s.risk_level === 'medium').length || 0, color: '#FFBB28' },
//     { name: 'High Risk', value: suppliers?.filter((s: any) => s.risk_level === 'high').length || 0, color: '#FF8042' },
//   ];

//   const regionalDistribution = suppliers?.reduce((acc: any[], supplier: any) => {
//     const region = supplier.region || 'Unknown';
//     const existing = acc.find(item => item.name === region);
//     if (existing) {
//       existing.value += 1;
//     } else {
//       acc.push({ name: region, value: 1 });
//     }
//     return acc;
//   }, [] as any[]) || [];

//   const esgScoreDistribution = [
//     { range: '0-20', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) <= 20).length || 0 },
//     { range: '21-40', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 20 && (s.esg_assessment_score || 0) <= 40).length || 0 },
//     { range: '41-60', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 40 && (s.esg_assessment_score || 0) <= 60).length || 0 },
//     { range: '61-80', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 60 && (s.esg_assessment_score || 0) <= 80).length || 0 },
//     { range: '81-100', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 80).length || 0 },
//   ];

//   const complianceData = [
//     { type: 'Environmental', compliant: suppliers?.filter((s: any) => s.environmental_compliance).length || 0, total: totalSuppliers },
//     { type: 'Social', compliant: suppliers?.filter((s: any) => s.social_compliance).length || 0, total: totalSuppliers },
//     { type: 'Governance', compliant: suppliers?.filter((s: any) => s.governance_compliance).length || 0, total: totalSuppliers },
//   ];

//   return (
//     <div className="space-y-6">
//       {/* Key Metrics */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
//         <Card>
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-sm font-medium text-gray-600">Total Suppliers</p>
//                 <p className="text-3xl font-bold">{totalSuppliers}</p>
//                 <p className="text-sm text-green-600 flex items-center mt-1">
//                   <CheckCircle className="h-4 w-4 mr-1" />
//                   {activeSuppliers} active
//                 </p>
//               </div>
//               <Users className="h-8 w-8 text-blue-600" />
//             </div>
//           </CardContent>
//         </Card>

//         <Card>
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-sm font-medium text-gray-600">Avg ESG Score</p>
//                 <p className="text-3xl font-bold">{avgESGScore.toFixed(1)}</p>
//                 <p className="text-sm text-gray-600 mt-1">Out of 100</p>
//               </div>
//               <TrendingUp className="h-8 w-8 text-green-600" />
//             </div>
//           </CardContent>
//         </Card>

//         <Card>
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-sm font-medium text-gray-600">High Risk</p>
//                 <p className="text-3xl font-bold text-red-600">{highRiskSuppliers}</p>
//                 <p className="text-sm text-gray-600 mt-1">Suppliers</p>
//               </div>
//               <AlertTriangle className="h-8 w-8 text-red-600" />
//             </div>
//           </CardContent>
//         </Card>

//         <Card>
//           <CardContent className="p-6">
//             <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-sm font-medium text-gray-600">Fully Compliant</p>
//                 <p className="text-3xl font-bold text-green-600">{compliantSuppliers}</p>
//                 <p className="text-sm text-gray-600 mt-1">Suppliers</p>
//               </div>
//               <CheckCircle className="h-8 w-8 text-green-600" />
//             </div>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Charts */}
//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//         {/* Risk Distribution */}
//         <Card>
//           <CardHeader>
//             <CardTitle>Risk Level Distribution</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <ResponsiveContainer width="100%" height={300}>
//               <PieChart>
//                 <Pie
//                   data={riskDistribution}
//                   cx="50%"
//                   cy="50%"
//                   outerRadius={80}
//                   fill="#8884d8"
//                   dataKey="value"
//                   label={({ name, value }) => `${name}: ${value}`}
//                 >
//                   {riskDistribution.map((entry, index) => (
//                     <Cell key={`cell-${index}`} fill={entry.color} />
//                   ))}
//                 </Pie>
//                 <Tooltip />
//               </PieChart>
//             </ResponsiveContainer>
//           </CardContent>
//         </Card>

//         {/* Regional Distribution */}
//         <Card>
//           <CardHeader>
//             <CardTitle>Regional Distribution</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <ResponsiveContainer width="100%" height={300}>
//               <BarChart data={regionalDistribution}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="name" />
//                 <YAxis />
//                 <Tooltip />
//                 <Bar dataKey="value" fill="#8884d8" />
//               </BarChart>
//             </ResponsiveContainer>
//           </CardContent>
//         </Card>

//         {/* ESG Score Distribution */}
//         <Card>
//           <CardHeader>
//             <CardTitle>ESG Score Distribution</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <ResponsiveContainer width="100%" height={300}>
//               <BarChart data={esgScoreDistribution}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="range" />
//                 <YAxis />
//                 <Tooltip />
//                 <Bar dataKey="count" fill="#00C49F" />
//               </BarChart>
//             </ResponsiveContainer>
//           </CardContent>
//         </Card>

//         {/* Compliance Analysis */}
//         <Card>
//           <CardHeader>
//             <CardTitle>Compliance Analysis</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <div className="space-y-4">
//               {complianceData.map((item) => (
//                 <div key={item.type} className="space-y-2">
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm font-medium">{item.type}</span>
//                     <span className="text-sm text-gray-600">
//                       {item.compliant}/{item.total} ({item.total > 0 ? Math.round((item.compliant / item.total) * 100) : 0}%)
//                     </span>
//                   </div>
//                   <div className="w-full bg-gray-200 rounded-full h-2">
//                     <div
//                       className="bg-green-600 h-2 rounded-full"
//                       style={{
//                         width: `${item.total > 0 ? (item.compliant / item.total) * 100 : 0}%`
//                       }}
//                     ></div>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Emissions & Ratings Summary */}
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//         <Card>
//           <CardHeader>
//             <CardTitle>EcoVadis Average</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <div className="text-3xl font-bold">{avgEcoVadis.toFixed(1)}</div>
//             <div className="text-sm text-gray-600">Across assessed suppliers</div>
//           </CardContent>
//         </Card>
//         <Card>
//           <CardHeader>
//             <CardTitle>Total Scope 1+2 Emissions</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <div className="text-3xl font-bold">{(totalScope1 + totalScope2).toLocaleString()}</div>
//             <div className="text-sm text-gray-600">tCO2e</div>
//           </CardContent>
//         </Card>
//         <Card>
//           <CardHeader>
//             <CardTitle>Total Scope 3 Emissions</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <div className="text-3xl font-bold">{totalScope3.toLocaleString()}</div>
//             <div className="text-sm text-gray-600">tCO2e</div>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Recent Assessments Summary */}
//       <Card>
//         <CardHeader>
//           <CardTitle>Assessment Summary</CardTitle>
//         </CardHeader>
//         <CardContent>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//             <div className="text-center">
//               <div className="text-2xl font-bold text-blue-600">
//                 {assessments?.length || 0}
//               </div>
//               <div className="text-sm text-gray-600">Total Assessments</div>
//             </div>
//             <div className="text-center">
//               <div className="text-2xl font-bold text-green-600">
//                 {assessments?.filter((a: any) => a.status === 'completed').length || 0}
//               </div>
//               <div className="text-sm text-gray-600">Completed</div>
//             </div>
//             <div className="text-center">
//               <div className="text-2xl font-bold text-yellow-600">
//                 {assessments?.filter((a: any) => a.status === 'pending').length || 0}
//               </div>
//               <div className="text-sm text-gray-600">Pending</div>
//             </div>
//           </div>
//         </CardContent>
//       </Card>

//       {/* Top Performing Suppliers */}
//       {suppliers && suppliers.length > 0 && (
//         <Card>
//           <CardHeader>
//             <CardTitle>Top Performing Suppliers</CardTitle>
//           </CardHeader>
//           <CardContent>
//             <div className="space-y-4">
//               {suppliers
//                 .filter((s: any) => s.esg_assessment_score)
//                 .sort((a: any, b: any) => (b.esg_assessment_score || 0) - (a.esg_assessment_score || 0))
//                 .slice(0, 5)
//                 .map((supplier: any) => (
//                   <div key={supplier.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
//                     <div>
//                       <h4 className="font-medium">{supplier.supplier_name}</h4>
//                       <p className="text-sm text-gray-600">{supplier.industry_sector}</p>
//                     </div>
//                     <div className="text-right">
//                       <div className="text-lg font-bold text-green-600">
//                         {supplier.esg_assessment_score}
//                       </div>
//                       <div className="text-sm text-gray-600">ESG Score</div>
//                     </div>
//                   </div>
//                 ))}
//             </div>
//           </CardContent>
//         </Card>
//       )}
//     </div>
//   );
// };


import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Users, Globe, Shield, Leaf, Target, BarChart3, Droplets, Zap, Trash2, AlertCircle } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const SupplyChainAnalytics = () => {
  const { user } = useAuth();

  // Fetch suppliers from Supabase
  const { data: suppliers, isLoading: suppliersLoading } = useQuery({
    queryKey: ['suppliers-analytics', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching suppliers:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Fetch assessments from Supabase (using supply_chain_assessments table)
  const { data: assessments, isLoading: assessmentsLoading } = useQuery({
    queryKey: ['assessments-analytics', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('supply_chain_assessments')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching assessments:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Derived metrics from assessments for enhanced analytics
  const assessmentsCount = assessments?.length || 0;
  const ghgDisclosureRate = assessmentsCount 
    ? Math.round((assessments.filter((a: any) => a.ghg_disclosed).length / assessmentsCount) * 100) 
    : 0;
  const emsAdoptionRate = assessmentsCount 
    ? Math.round((assessments.filter((a: any) => a.has_ems).length / assessmentsCount) * 100) 
    : 0;
  const sbtiCommitRate = assessmentsCount 
    ? Math.round((assessments.filter((a: any) => a.sbti_committed).length / assessmentsCount) * 100) 
    : 0;
  const capAdoptionRate = assessmentsCount 
    ? Math.round((assessments.filter((a: any) => a.cap_in_place).length / assessmentsCount) * 100) 
    : 0;
  const avgEcoVadis = assessmentsCount 
    ? (assessments.reduce((sum: number, a: any) => sum + (Number(a.ecovadis_score) || 0), 0) / assessmentsCount) 
    : 0;
  
  // Calculate emissions totals
  const totalScope1 = assessments?.reduce((sum: number, a: any) => sum + (Number(a.scope1_emissions) || 0), 0) || 0;
  const totalScope2 = assessments?.reduce((sum: number, a: any) => sum + (Number(a.scope2_emissions) || 0), 0) || 0;
  const totalScope3 = assessments?.reduce((sum: number, a: any) => sum + (Number(a.scope3_emissions) || 0), 0) || 0;
  
  // Calculate resource consumption totals
  const totalEnergy = assessments?.reduce((sum: number, a: any) => sum + (Number(a.energy_consumption_mwh) || 0), 0) || 0;
  const totalWater = assessments?.reduce((sum: number, a: any) => sum + (Number(a.water_withdrawal_m3) || 0), 0) || 0;
  const totalWaste = assessments?.reduce((sum: number, a: any) => sum + (Number(a.waste_generated_tons) || 0), 0) || 0;
  const totalIncidents = assessments?.reduce((sum: number, a: any) => sum + (Number(a.incidents_count) || 0), 0) || 0;

  // Policy adoption rates
  const policyData = [
    { 
      name: 'Env Policy', 
      value: assessmentsCount ? Math.round((assessments.filter((a: any) => a.policies_environmental).length / assessmentsCount) * 100) : 0,
      icon: <Leaf className="h-4 w-4" />
    },
    { 
      name: 'Human Rights', 
      value: assessmentsCount ? Math.round((assessments.filter((a: any) => a.policies_human_rights).length / assessmentsCount) * 100) : 0,
      icon: <Shield className="h-4 w-4" />
    },
    { 
      name: 'Anti-Corruption', 
      value: assessmentsCount ? Math.round((assessments.filter((a: any) => a.policies_anti_corruption).length / assessmentsCount) * 100) : 0,
      icon: <Shield className="h-4 w-4" />
    },
    { 
      name: 'Data Privacy', 
      value: assessmentsCount ? Math.round((assessments.filter((a: any) => a.data_privacy_policy).length / assessmentsCount) * 100) : 0,
      icon: <Shield className="h-4 w-4" />
    },
    { 
      name: 'Whistleblower', 
      value: assessmentsCount ? Math.round((assessments.filter((a: any) => a.whistleblower_mechanism).length / assessmentsCount) * 100) : 0,
      icon: <Shield className="h-4 w-4" />
    },
  ];

  // CDP Score distribution
  const cdpDistribution = assessments?.reduce((acc: any[], assessment: any) => {
    const score = assessment.cdp_score || 'Not Rated';
    const existing = acc.find(item => item.name === score);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: score, value: 1 });
    }
    return acc;
  }, [] as any[]) || [];

  // Calculate supplier metrics (from existing code)
  const totalSuppliers = suppliers?.length || 0;
  const activeSuppliers = suppliers?.filter(s => s.status === 'active').length || 0;
  const avgESGScore = suppliers?.length 
    ? suppliers.reduce((sum: number, s: any) => sum + (s.esg_assessment_score || 0), 0) / suppliers.length
    : 0;
  
  const highRiskSuppliers = suppliers?.filter((s: any) => s.risk_level === 'high').length || 0;
  const compliantSuppliers = suppliers?.filter((s: any) => 
    s.environmental_compliance && s.social_compliance && s.governance_compliance
  ).length || 0;

  // Prepare chart data
  const riskDistribution = [
    { name: 'Low Risk', value: suppliers?.filter((s: any) => s.risk_level === 'low').length || 0, color: '#00C49F' },
    { name: 'Medium Risk', value: suppliers?.filter((s: any) => s.risk_level === 'medium').length || 0, color: '#FFBB28' },
    { name: 'High Risk', value: suppliers?.filter((s: any) => s.risk_level === 'high').length || 0, color: '#FF8042' },
  ];

  const regionalDistribution = suppliers?.reduce((acc: any[], supplier: any) => {
    const region = supplier.region || 'Unknown';
    const existing = acc.find(item => item.name === region);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: region, value: 1 });
    }
    return acc;
  }, [] as any[]) || [];

  const esgScoreDistribution = [
    { range: '0-20', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) <= 20).length || 0 },
    { range: '21-40', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 20 && (s.esg_assessment_score || 0) <= 40).length || 0 },
    { range: '41-60', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 40 && (s.esg_assessment_score || 0) <= 60).length || 0 },
    { range: '61-80', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 60 && (s.esg_assessment_score || 0) <= 80).length || 0 },
    { range: '81-100', count: suppliers?.filter((s: any) => (s.esg_assessment_score || 0) > 80).length || 0 },
  ];

  const complianceData = [
    { type: 'Environmental', compliant: suppliers?.filter((s: any) => s.environmental_compliance).length || 0, total: totalSuppliers },
    { type: 'Social', compliant: suppliers?.filter((s: any) => s.social_compliance).length || 0, total: totalSuppliers },
    { type: 'Governance', compliant: suppliers?.filter((s: any) => s.governance_compliance).length || 0, total: totalSuppliers },
  ];

  if (suppliersLoading || assessmentsLoading) {
    return <div className="flex justify-center p-8">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Suppliers</p>
                <p className="text-3xl font-bold">{totalSuppliers}</p>
                <p className="text-sm text-green-600 flex items-center mt-1">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {activeSuppliers} active
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg ESG Score</p>
                <p className="text-3xl font-bold">{avgESGScore.toFixed(1)}</p>
                <p className="text-sm text-gray-600 mt-1">Out of 100</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Risk</p>
                <p className="text-3xl font-bold text-red-600">{highRiskSuppliers}</p>
                <p className="text-sm text-gray-600 mt-1">Suppliers</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Fully Compliant</p>
                <p className="text-3xl font-bold text-green-600">{compliantSuppliers}</p>
                <p className="text-sm text-gray-600 mt-1">Suppliers</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Sustainability Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">GHG Disclosure</p>
                <p className="text-2xl font-bold">{ghgDisclosureRate}%</p>
              </div>
              <BarChart3 className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">EMS Adoption</p>
                <p className="text-2xl font-bold">{emsAdoptionRate}%</p>
              </div>
              <Leaf className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">SBTi Committed</p>
                <p className="text-2xl font-bold">{sbtiCommitRate}%</p>
              </div>
              <Target className="h-6 w-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CAP in Place</p>
                <p className="text-2xl font-bold">{capAdoptionRate}%</p>
              </div>
              <CheckCircle className="h-6 w-6 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg EcoVadis</p>
                <p className="text-2xl font-bold">{avgEcoVadis.toFixed(1)}</p>
              </div>
              <Shield className="h-6 w-6 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Risk Level Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={riskDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {riskDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Regional Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Regional Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={regionalDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* ESG Score Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>ESG Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={esgScoreDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Policy Adoption Rates */}
        <Card>
          <CardHeader>
            <CardTitle>Policy Adoption Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={policyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}%`, 'Adoption Rate']} />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Emissions & Resource Consumption */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              <CardTitle>Total Emissions</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Scope 1 (Direct)</span>
                <span className="text-lg font-bold">{totalScope1.toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-600">tCO2e</div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Scope 2 (Indirect)</span>
                <span className="text-lg font-bold">{totalScope2.toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-600">tCO2e</div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Scope 3 (Value Chain)</span>
                <span className="text-lg font-bold">{totalScope3.toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-600">tCO2e</div>
            </div>
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Emissions</span>
                <span className="text-xl font-bold text-blue-600">
                  {(totalScope1 + totalScope2 + totalScope3).toLocaleString()}
                </span>
              </div>
              <div className="text-sm text-gray-600">tCO2e</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              <CardTitle>Resource Consumption</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Energy</span>
                <span className="text-lg font-bold">{totalEnergy.toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-600">MWh</div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Water</span>
                <span className="text-lg font-bold">{totalWater.toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-600">m³</div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Waste</span>
                <span className="text-lg font-bold">{totalWaste.toLocaleString()}</span>
              </div>
              <div className="text-sm text-gray-600">tons</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>Incidents & Compliance</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Incidents</span>
                <span className="text-lg font-bold">{totalIncidents}</span>
              </div>
              <div className="text-sm text-gray-600">Health, safety & environmental</div>
            </div>
            <div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Avg per Supplier</span>
                <span className="text-lg font-bold">
                  {assessmentsCount > 0 ? (totalIncidents / assessmentsCount).toFixed(1) : 0}
                </span>
              </div>
            </div>
            <div className="pt-4">
              <div className="text-sm font-medium mb-2">Supplier Compliance</div>
              <div className="space-y-2">
                {complianceData.map((item) => (
                  <div key={item.type} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{item.type}</span>
                    <span className="text-sm font-medium">
                      {item.compliant}/{item.total} ({item.total > 0 ? Math.round((item.compliant / item.total) * 100) : 0}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CDP Score Distribution */}
      {cdpDistribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>CDP Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {cdpDistribution.map((item, index) => (
                <div key={index} className="text-center">
                  <div className={`text-2xl font-bold ${
                    item.name === 'A' || item.name === 'A-' ? 'text-green-600' :
                    item.name === 'B' || item.name === 'B-' ? 'text-blue-600' :
                    item.name === 'C' || item.name === 'C-' ? 'text-yellow-600' :
                    item.name === 'D' || item.name === 'D-' ? 'text-orange-600' :
                    item.name === 'F' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {item.name}
                  </div>
                  <div className="text-sm text-gray-600">{item.value} supplier{item.value !== 1 ? 's' : ''}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Assessments Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Assessment Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {assessments?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Total Assessments</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {assessments?.filter((a: any) => a.status === 'completed').length || 0}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {assessments?.filter((a: any) => a.status === 'pending').length || 0}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {assessments?.filter((a: any) => a.status === 'in_progress').length || 0}
              </div>
              <div className="text-sm text-gray-600">In Progress</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Performing Suppliers */}
      {suppliers && suppliers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Suppliers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {suppliers
                .filter((s: any) => s.esg_assessment_score)
                .sort((a: any, b: any) => (b.esg_assessment_score || 0) - (a.esg_assessment_score || 0))
                .slice(0, 5)
                .map((supplier: any) => (
                  <div key={supplier.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium">{supplier.supplier_name}</h4>
                      <p className="text-sm text-gray-600">{supplier.industry_sector}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline">{supplier.risk_level || 'Medium'}</Badge>
                        {supplier.region && (
                          <Badge variant="secondary" className="text-xs">
                            {supplier.region}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">
                        {supplier.esg_assessment_score}
                      </div>
                      <div className="text-sm text-gray-600">ESG Score</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Status: <span className={supplier.status === 'active' ? 'text-green-600' : 'text-yellow-600'}>
                          {supplier.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
